import { FieldPath, runTransaction, serverTimestamp, type DocumentData } from "firebase/firestore";
import { AppError, toAppError } from "@/data/errors";
import { requireManageAccess } from "@/lib/access-control";
import { auth } from "@/lib/firebase-auth";
import { settingsChanges, type EditableSettings } from "@/domain/settings";
import { paths } from "./paths";

function valueAt(data: DocumentData, path: string[]): unknown {
  let value: unknown = data;
  for (const key of path)
    value =
      value && typeof value === "object" ? (value as Record<string, unknown>)[key] : undefined;
  return value;
}
/** Settings never write Context, executable models, publication revisions or leases. */
export async function saveSettings(
  customerId: string,
  baseline: EditableSettings,
  edited: EditableSettings,
): Promise<void> {
  try {
    const changes = settingsChanges(baseline, edited);
    if (!changes.length) return;
    const actor = await requireManageAccess(customerId);
    await runTransaction(paths.customer(customerId).firestore, async (tx) => {
      const access = await requireManageAccess(customerId, tx);
      if (access.userId !== actor.userId || auth.currentUser?.uid !== actor.userId)
        throw new AppError("access", "The editor identity changed.");
      const account = await tx.get(paths.customer(customerId));
      const business = await tx.get(paths.business(customerId));
      if (!account.exists() || !business.exists())
        throw new AppError("missing-document", "Workspace settings are unavailable.");
      for (const change of changes) {
        const current = valueAt(
          change.document === "customer" ? account.data() : business.data(),
          change.path,
        );
        // Match the account reader's defaults for historical notification fields.
        const comparable =
          change.document === "customer" && change.path[1] === "leadEmail"
            ? (current ?? "")
            : change.document === "customer" && current === undefined
              ? false
              : current;
        if (comparable !== change.before && comparable !== change.after)
          throw new AppError(
            "conflict",
            "These settings changed in another session. Reload the page before saving; your edits have been kept.",
          );
      }
      const transactionChanges = [...changes];
      // An authorized save also materializes the empty default on legacy accounts.
      if (
        valueAt(account.data(), ["notificationPreferences", "leadEmail"]) == null &&
        edited.notifications.leadEmail === "" &&
        !changes.some((change) => change.document === "customer" && change.path[1] === "leadEmail")
      ) {
        transactionChanges.push({
          document: "customer",
          path: ["notificationPreferences", "leadEmail"],
          before: "",
          after: "",
        });
      }
      for (const document of ["business", "customer"] as const) {
        const changed = transactionChanges.filter(
          (change) =>
            change.document === document &&
            valueAt(document === "customer" ? account.data() : business.data(), change.path) !==
              change.after,
        );
        if (!changed.length) continue;
        const fields: unknown[] = [];
        for (const change of changed) fields.push(new FieldPath(...change.path), change.after);
        tx.update(
          document === "customer" ? paths.customer(customerId) : paths.business(customerId),
          new FieldPath("updatedAt"),
          serverTimestamp(),
          ...fields,
        );
      }
    });
  } catch (error) {
    throw toAppError(error, "write-failed");
  }
}
